/**
 * Contextual help content, keyed by Vue route name.
 *
 * Each route can expose a list of tips displayed at the top of the
 * HelpWidget panel under "Sur cette page". Add a new entry here when a
 * page deserves in-context guidance; the widget gracefully hides the
 * section when the current route has no entry.
 *
 * Shape: { [routeName: string]: Tip[] }
 * Tip:   { icon, title, description }
 */
const help = {
    // --- News ---
    News: [
        {
            icon: 'fal fa-filter',
            title: 'Filtrer vos actualités',
            description:
                'Combinez les filtres statut (brouillon / publié) et catégorie pour retrouver rapidement un article. La recherche textuelle porte sur le titre.'
        },
        {
            icon: 'fal fa-pen',
            title: 'Éditer un article',
            description:
                'Cliquez sur le titre ou sur l’icône crayon pour ouvrir l’édition. Les actions publier / dépublier sont disponibles ligne par ligne.'
        },
        {
            icon: 'fal fa-tags',
            title: 'Organiser en catégories',
            description:
                'Le bouton « Gérer les catégories » en haut à droite ouvre la gestion — utile pour regrouper et classer vos actualités.'
        }
    ],

    NewsCreate: [
        {
            icon: 'fal fa-language',
            title: 'FR obligatoire, EN optionnel',
            description:
                'La version française doit être renseignée pour publier. Les pastilles colorées dans les onglets vous montrent la complétion de chaque langue.'
        },
        {
            icon: 'fal fa-image',
            title: 'Image de couverture',
            description:
                'Acceptée jusqu’à 5 Mo (JPEG / PNG / WebP). Elle sert dans la liste publique et dans les partages sociaux.'
        },
        {
            icon: 'fal fa-save',
            title: 'Brouillon vs Publier',
            description:
                '« Brouillon » enregistre sans rendre l’article public. « Publier » enregistre et rend immédiatement disponible sur le site public.'
        }
    ],

    NewsEdit: [
        {
            icon: 'fal fa-language',
            title: 'Versions FR et EN',
            description:
                'Passez d’un onglet à l’autre pour éditer chaque langue. Les pastilles indiquent la complétion.'
        },
        {
            icon: 'fal fa-eye-slash',
            title: 'Dépublier un article',
            description:
                'Si l’article est publié, le bouton « Dépublier » le repasse en brouillon — il disparaît alors du site public sans être supprimé.'
        },
        {
            icon: 'fal fa-trash',
            title: 'Suppression = soft delete',
            description:
                'La suppression est réversible côté back-office sur demande. L’article cesse immédiatement d’être visible côté public.'
        }
    ],

    NewsCategories: [
        {
            icon: 'fal fa-tag',
            title: 'Nom FR obligatoire',
            description:
                'Le nom anglais est optionnel ; s’il est vide, la liste publique affichera le nom français pour les visiteurs en EN.'
        },
        {
            icon: 'fal fa-toggle-on',
            title: 'Activer / désactiver',
            description:
                'Une catégorie inactive reste liée aux articles existants mais n’est plus proposée à la création d’un nouvel article.'
        }
    ],

    // --- Tickets ---
    Data: [
        {
            icon: 'fal fa-eye',
            title: 'Vue rapide sans quitter la liste',
            description:
                'L’icône œil en fin de ligne ouvre un drawer latéral avec les infos essentielles du ticket. Pratique pour trier une longue liste.'
        },
        {
            icon: 'fal fa-bell',
            title: 'Relancer un opérateur',
            description:
                'L’icône cloche envoie une notification au responsable assigné — utile quand un ticket stagne.'
        },
        {
            icon: 'fal fa-file-export',
            title: 'Exporter la sélection',
            description:
                'Le bouton « Exporter » en haut à droite prend en compte les filtres actifs et télécharge un CSV des tickets filtrés.'
        }
    ],

    Post: [
        {
            icon: 'fal fa-comments',
            title: 'Échanges avec le citoyen',
            description:
                'Utilisez la zone d’échanges pour répondre au citoyen. Les modèles de réponse permettent de pré-remplir le message en un clic.'
        },
        {
            icon: 'fal fa-flag',
            title: 'Statut et priorité',
            description:
                'Cliquez sur les pastilles statut / priorité en haut du ticket pour les modifier — l’historique est conservé automatiquement.'
        },
        {
            icon: 'fal fa-paperclip',
            title: 'Joindre des fichiers',
            description:
                'Le bouton « Joindre » ajoute une pièce jointe à votre prochain message. Le citoyen la recevra par notification.'
        }
    ],

    Kanban: [
        {
            icon: 'fal fa-arrows',
            title: 'Glisser-déposer',
            description:
                'Faites glisser un ticket d’une colonne à l’autre pour changer son statut. L’appel API est lancé automatiquement.'
        },
        {
            icon: 'fal fa-filter',
            title: 'Filtres respectés',
            description:
                'Les filtres sélectionnés en haut s’appliquent sur toutes les colonnes simultanément.'
        }
    ],

    TicketMap: [
        {
            icon: 'fal fa-map-marker-alt',
            title: 'Ticket cliquable',
            description:
                'Cliquez sur un marqueur pour ouvrir un aperçu du ticket. La puce colorée reflète son statut actuel.'
        },
        {
            icon: 'fal fa-layer-group',
            title: 'Clusters de proximité',
            description:
                'Les tickets regroupés par zone apparaissent en cluster numéroté — zoomez pour les éclater individuellement.'
        }
    ],

    // --- Form builder ---
    CustomFormBuilder: [
        {
            icon: 'fal fa-route',
            title: 'Navigation par étape',
            description:
                'Le stepper en haut vous permet de naviguer entre les étapes du builder — vos modifications sont conservées entre les étapes.'
        },
        {
            icon: 'fal fa-cubes',
            title: 'Panneau des champs',
            description:
                'Glissez un champ depuis la palette de gauche vers le canvas central pour l’ajouter à votre formulaire.'
        }
    ],

    // --- Company admin ---
    CompanyPermissions: [
        {
            icon: 'fal fa-shield-check',
            title: 'Modifier un rôle',
            description:
                'Sélectionnez un rôle à gauche, cochez / décochez les permissions, puis « Enregistrer » applique les changements à tous les utilisateurs de ce rôle.'
        },
        {
            icon: 'fal fa-layer-group',
            title: 'Permissions groupées par module',
            description:
                'Les cases sont regroupées par feature (Tickets, Actualités, Formulaires…). Les modules n’apparaissent que si le back les expose dans /roles/defaults.'
        }
    ],

    CompanySettings: [
        {
            icon: 'fal fa-building',
            title: 'Identité de la commune',
            description:
                'Le nom, le logo et les couleurs définis ici sont repris dans toutes les communications automatiques envoyées aux citoyens.'
        }
    ],

    CompanyUsers: [
        {
            icon: 'fal fa-user-plus',
            title: 'Créer un utilisateur',
            description:
                'Les nouveaux utilisateurs reçoivent un email d’invitation. Le rôle attribué détermine leurs permissions (voir la page Permissions).'
        },
        {
            icon: 'fal fa-user-lock',
            title: 'Désactiver plutôt que supprimer',
            description:
                'Désactiver un utilisateur conserve l’historique de ses actions tout en bloquant son accès. Préférez cette option à la suppression.'
        }
    ],

    CompanyWebhooks: [
        {
            icon: 'fal fa-bolt',
            title: 'Déclenché sur événement',
            description:
                'Un webhook est appelé à chaque occurrence de l’événement choisi (nouveau ticket, changement de statut, message citoyen…).'
        },
        {
            icon: 'fal fa-lock',
            title: 'Signature et sécurité',
            description:
                'Vérifiez côté réception la signature du webhook pour vous assurer que la requête vient bien de BougeMaVille.'
        }
    ],

    BackofficeInfos: [
        {
            icon: 'fal fa-sitemap',
            title: 'Vue d’ensemble',
            description:
                'Cette page résume votre configuration : types de formulaires, statuts, RACI, déploiements et champs. Utile pour un audit rapide.'
        }
    ],

    // --- Tickets (wizards) ---
    FormChoice: [
        {
            icon: 'fal fa-layer-group',
            title: 'Choix du formulaire',
            description:
                'Chaque formulaire représente un type de demande spécifique. Les formulaires inactifs n’apparaissent pas ici.'
        }
    ],

    CreatePost: [
        {
            icon: 'fal fa-route',
            title: 'Navigation par étape',
            description:
                'Le wizard vous guide étape par étape. Les champs marqués d’un astérisque sont obligatoires pour passer à la suite.'
        },
        {
            icon: 'fal fa-map-marker-alt',
            title: 'Localisation',
            description:
                'Si le formulaire a une restriction géographique, choisissez une adresse à l’intérieur de la zone autorisée — sinon le bouton « Suivant » restera inactif.'
        },
        {
            icon: 'fal fa-paperclip',
            title: 'Pièces jointes',
            description:
                'Taille max par fichier définie par le formulaire. Plusieurs fichiers peuvent être ajoutés sur un même champ upload.'
        }
    ],

    EditPost: [
        {
            icon: 'fal fa-save',
            title: 'Modifications immédiates',
            description:
                'Les changements sont enregistrés dès validation du wizard. L’historique du ticket garde la trace des modifications.'
        }
    ],

    // --- Stats & performance ---
    Statistiques: [
        {
            icon: 'fal fa-chart-line',
            title: 'Volumétrie',
            description:
                'Répartition des tickets par période, formulaire et statut. Utilisez le filtre temporel pour ajuster la fenêtre d’analyse.'
        },
        {
            icon: 'fal fa-download',
            title: 'Export des données',
            description:
                'Les graphiques peuvent être exportés ou les données brutes téléchargées pour analyse hors-ligne.'
        }
    ],

    Performances: [
        {
            icon: 'fal fa-stopwatch',
            title: 'Temps par statut',
            description:
                'Identifiez les étapes où vos tickets stagnent. Les valeurs en rouge indiquent un goulot d’étranglement à investiguer.'
        }
    ],

    // --- Home / dashboard ---
    Home: [
        {
            icon: 'fal fa-tachometer-alt',
            title: 'Tableau de bord',
            description:
                'Vue synthétique des tickets récents et des indicateurs clés. Les cards sont cliquables — elles mènent à la liste filtrée correspondante.'
        },
        {
            icon: 'fal fa-bars',
            title: 'Sidebar de navigation',
            description:
                'Tous les modules (Tickets, Formulaires, Actualités, Équipe…) sont accessibles via la sidebar à gauche. En mode replié, survolez une icône pour faire apparaître son sous-menu.'
        }
    ],

    // --- Response templates ---
    Templates: [
        {
            icon: 'fal fa-reply',
            title: 'Modèles de réponse',
            description:
                'Les modèles pré-remplissent les messages envoyés aux citoyens depuis la zone d’échanges d’un ticket. Utile pour les réponses récurrentes.'
        },
        {
            icon: 'fal fa-code',
            title: 'Variables dynamiques',
            description:
                'Les modèles peuvent inclure des variables (nom du citoyen, référence du ticket) automatiquement substituées à l’envoi.'
        }
    ],

    CreateTemplate: [
        {
            icon: 'fal fa-language',
            title: 'Traduction FR / EN',
            description:
                'Le modèle est défini par langue. Si un modèle EN est absent, la version FR est utilisée en fallback.'
        }
    ],

    EditTemplate: [
        {
            icon: 'fal fa-language',
            title: 'Traduction FR / EN',
            description:
                'Modifier un modèle impacte tous les futurs envois qui l’utilisent. Les messages déjà envoyés restent inchangés.'
        }
    ],

    // --- Forms ---
    FormTypes: [
        {
            icon: 'fal fa-layer-group',
            title: 'Types de formulaires',
            description:
                'Les types regroupent vos formulaires (propreté, voirie, demandes administratives…). Un type inactif masque ses formulaires dans la navigation.'
        }
    ],

    Forms: [
        {
            icon: 'fal fa-poll',
            title: 'Vos formulaires',
            description:
                'Cette liste regroupe tous les formulaires créés. Le bouton + en haut lance le builder pour en créer un nouveau.'
        },
        {
            icon: 'fal fa-copy',
            title: 'Dupliquer pour itérer',
            description:
                'Dupliquez un formulaire existant pour tester une variante sans toucher à celui en production.'
        }
    ],

    FormTypeSelect: [
        {
            icon: 'fal fa-layer-group',
            title: 'Sélectionnez un type',
            description:
                'Le type choisi détermine la catégorie dans laquelle votre formulaire apparaîtra pour les citoyens.'
        }
    ],

    FormTypeDetail: [
        {
            icon: 'fal fa-info-circle',
            title: 'Détail du type',
            description:
                'Configurez ici le nom, l’icône et les statuts applicables aux tickets issus de ce type de formulaire.'
        }
    ],

    // --- My profile ---
    MyProfile: [
        {
            icon: 'fal fa-user-edit',
            title: 'Vos informations',
            description:
                'Vous pouvez modifier votre nom, votre email, votre mot de passe et vos préférences de notification.'
        }
    ],

    // --- Super-admin ---
    AdminCompanies: [
        {
            icon: 'fal fa-building',
            title: 'Communes du réseau',
            description:
                'Liste de toutes les communes de la plateforme. Cliquez sur une ligne pour basculer (impersonate) vers son back-office.'
        },
        {
            icon: 'fal fa-plus-circle',
            title: 'Ajouter une commune',
            description:
                'La création d’une nouvelle commune initialise automatiquement les rôles et permissions par défaut.'
        }
    ]
}

// --- Extra content ---

help.UserProfile = [
    {
        icon: 'fal fa-id-badge',
        title: 'Profil complet',
        description:
            'Retrouvez ici l’activité de l’utilisateur (tickets traités, dernière connexion) en plus de ses informations personnelles.'
    },
    {
        icon: 'fal fa-shield-alt',
        title: 'Rôle & permissions',
        description:
            'Le rôle indiqué ici détermine ce que l’utilisateur peut voir et faire. Modifiez le rôle global côté page Permissions.'
    }
]

help.Form = [
    {
        icon: 'fal fa-poll',
        title: 'Aperçu du formulaire',
        description:
            'Cette page expose le détail du formulaire : ses champs, ses statuts et ses RACI. Les modifications se font via le builder.'
    }
]

help.EditForm = [
    {
        icon: 'fal fa-pencil',
        title: 'Édition simplifiée',
        description:
            'Cet écran permet d’éditer les métadonnées du formulaire (nom, type, icône). Pour modifier les champs et les étapes, ouvrez le builder complet.'
    }
]

help.FormWizard = [
    {
        icon: 'fal fa-route',
        title: 'Navigation par étape',
        description:
            'Le wizard vous guide étape par étape. Les champs marqués d’un astérisque sont obligatoires pour passer à la suite.'
    },
    {
        icon: 'fal fa-map-marker-alt',
        title: 'Localisation',
        description:
            'Si le formulaire a une restriction géographique, choisissez une adresse à l’intérieur de la zone autorisée — sinon le bouton « Suivant » restera inactif.'
    },
    {
        icon: 'fal fa-paperclip',
        title: 'Pièces jointes',
        description:
            'Taille max par fichier définie par le formulaire. Plusieurs fichiers peuvent être ajoutés sur un même champ upload.'
    }
]

help.CreateApplications = [
    {
        icon: 'fal fa-plug',
        title: 'Application du formulaire',
        description:
            'Associez ce formulaire à une zone géographique ou à un canal (mobile, widget web). Plusieurs applications peuvent coexister pour un même formulaire.'
    }
]

// --- Aliases: variant routes share the same content as their base route ---
help.PostsByFormType = help.Data
help.KanbanByFormType = help.Kanban
help.TicketMapByFormType = help.TicketMap
help.StatistiquesByFormType = help.Statistiques
help.PerformancesByFormType = help.Performances
help.CustomFormBuilderEdit = help.CustomFormBuilder
help.CreateCompanyUser = help.CompanyUsers
help.EditCompanyUser = help.CompanyUsers
help.CreateCompanyWebhook = help.CompanyWebhooks
help.EditCompanyWebhook = help.CompanyWebhooks
help.CreateCompany = help.AdminCompanies
help.Company = help.AdminCompanies
help.EditCompany = help.AdminCompanies
help.EditApplications = help.CreateApplications

export default help
